import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import axios from "axios";
import { ArrowLeft, Clock, Loader2, Lock, LogOut } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RecentOrders = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [loginForm, setLoginForm] = useState({ email: "", order_reference: "" });
  const [customerEmail, setCustomerEmail] = useState("");

  useEffect(() => {
    const savedEmail = localStorage.getItem("customerEmail");
    if (savedEmail) {
      setCustomerEmail(savedEmail);
      setIsLoggedIn(true);
      fetchOrders(savedEmail);
    } else {
      setLoading(false);
      setShowLogin(true);
    }
  }, []);

  const fetchOrders = async (email) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/orders/customer?email=${encodeURIComponent(email)}`);
      setOrders(res.data);
    } catch (error) {
      console.error("Failed to fetch orders:", error);
      toast.error("Failed to load orders");
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Verify customer by checking if they have orders with this email
      const res = await axios.post(`${API}/orders/verify-customer`, {
        email: loginForm.email,
        order_reference: loginForm.order_reference
      });
      
      if (res.data.verified) {
        localStorage.setItem("customerEmail", loginForm.email);
        setCustomerEmail(loginForm.email);
        setIsLoggedIn(true);
        setShowLogin(false);
        fetchOrders(loginForm.email);
        toast.success("Welcome back!");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid email or order reference");
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("customerEmail");
    setIsLoggedIn(false);
    setCustomerEmail("");
    setOrders([]);
    setShowLogin(true);
  };

  return (
    <div className="min-h-screen bg-[#F9F9F7]">
      {/* Navigation */}
      <nav className="nav-wrapper" data-testid="orders-nav">
        <div className="container-padding py-4 flex items-center justify-between max-w-7xl mx-auto">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 bg-[#0F3D2E] flex items-center justify-center">
              <span className="text-white font-semibold text-lg">S</span>
            </div>
            <span className="font-semibold text-lg tracking-tight">
              Stone Sills Direct
            </span>
          </Link>

          <div className="flex items-center gap-4">
            {isLoggedIn && (
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 text-sm text-[#57534E] hover:text-[#0F3D2E]"
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4" />
                Sign Out
              </button>
            )}
            <Link
              to="/"
              className="flex items-center gap-2 text-sm text-[#57534E] hover:text-[#0F3D2E]"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Link>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="container-padding max-w-4xl mx-auto py-12" data-testid="recent-orders-page">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="mb-8">
            <span className="section-caption text-[#C27C2E] mb-2 block">
              Order History
            </span>
            <h1 className="text-3xl md:text-4xl font-medium">Your Orders</h1>
            <p className="text-[#57534E] mt-2">
              {isLoggedIn ? `Viewing orders for ${customerEmail}` : "Sign in to view your orders"}
            </p>
          </div>

          {/* Login Form */}
          {showLogin && !isLoggedIn && (
            <div className="stone-card max-w-md mx-auto" data-testid="customer-login">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-[#0F3D2E] flex items-center justify-center">
                  <Lock className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-medium">View Your Orders</h2>
                  <p className="text-sm text-[#57534E]">Enter your details to access order history</p>
                </div>
              </div>

              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <Label className="input-label">Email Address</Label>
                  <Input
                    type="email"
                    required
                    value={loginForm.email}
                    onChange={(e) => setLoginForm(prev => ({ ...prev, email: e.target.value }))}
                    className="input-field"
                    placeholder="The email used for your order"
                    data-testid="customer-email"
                  />
                </div>
                <div>
                  <Label className="input-label">Order Reference</Label>
                  <Input
                    required
                    value={loginForm.order_reference}
                    onChange={(e) => setLoginForm(prev => ({ ...prev, order_reference: e.target.value }))}
                    className="input-field"
                    placeholder="e.g. QS-20260316-ABC123"
                    data-testid="order-reference"
                  />
                </div>
                <Button
                  type="submit"
                  className="btn-primary w-full"
                  disabled={loading}
                  data-testid="customer-login-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Verifying...
                    </>
                  ) : (
                    "View Orders"
                  )}
                </Button>
              </form>

              <p className="text-sm text-[#57534E] mt-4 text-center">
                Need your order reference? Check your confirmation email.
              </p>
            </div>
          )}

          {/* Orders List */}
          {isLoggedIn && (
            <>
              {loading ? (
                <div className="flex items-center justify-center py-16">
                  <Loader2 className="w-8 h-8 animate-spin text-[#0F3D2E]" />
                </div>
              ) : orders.length === 0 ? (
                <div className="stone-card text-center py-16" data-testid="no-orders">
                  <Clock className="w-12 h-12 text-[#57534E] mx-auto mb-4" />
                  <h3 className="text-xl font-medium mb-2">No Orders Found</h3>
                  <p className="text-[#57534E] mb-6">
                    You haven't placed any orders yet.
                  </p>
                  <Link
                    to="/calculator"
                    className="btn-primary inline-block px-6 py-3"
                  >
                    Get a Quote
                  </Link>
                </div>
              ) : (
                <div className="space-y-4">
                  {orders.map((order, index) => (
                    <motion.div
                      key={order.id}
                      className="stone-card"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: index * 0.05 }}
                      data-testid={`order-${order.id}`}
                    >
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div>
                          <div className="flex items-center gap-3 mb-2">
                            <span className="font-mono text-lg">{order.reference}</span>
                            <span
                              className={`px-2 py-1 text-xs uppercase ${
                                order.status === "completed"
                                  ? "bg-green-100 text-green-800"
                                  : "bg-yellow-100 text-yellow-800"
                              }`}
                            >
                              {order.status}
                            </span>
                          </div>
                          <p className="text-sm text-[#57534E]">
                            {order.length}mm x {order.depth}mm | Qty: {order.quantity} |{" "}
                            {order.edge_type?.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase()) || "Standard"}
                          </p>
                        </div>

                        <div className="text-right">
                          <div className="text-2xl font-medium text-[#0F3D2E]">
                            £{order.total_price?.toFixed(2)}
                          </div>
                          <div className="text-sm text-[#57534E]">
                            {new Date(order.created_at).toLocaleDateString("en-GB", {
                              day: "numeric",
                              month: "short",
                              year: "numeric",
                            })}
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default RecentOrders;
